import { createFileRoute, useNavigate, redirect, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { btnPrimary } from '@/components/rm'
import { BrandMark, BRAND_URL } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'

export const Route = createFileRoute('/signup')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (data.session) throw redirect({ to: '/dashboard' })
  },
  component: SignupPage,
})

const inputCls =
  'h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] text-token-primary outline-none transition-colors focus:border-token-strong focus:ring-2 focus:ring-brand/10'

function SignupPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const [f, setF] = useState({ businessName: '', firstName: '', lastName: '', email: '', password: '' })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((s) => ({ ...s, [k]: e.target.value }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!f.businessName || !f.firstName || !f.email || f.password.length < 8) {
      return setError(t('signUp.missing'))
    }
    setBusy(true)
    const { data, error: err } = await supabase.functions.invoke('signup', { body: f })
    if (err || !(data as { ok?: boolean })?.ok) {
      setBusy(false)
      const ctx = (err as { context?: Response } | null)?.context
      let msg = t('signUp.failed')
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error } } catch { /* */ }
      return setError(msg)
    }
    // Account created — sign straight in.
    const { error: signErr } = await supabase.auth.signInWithPassword({ email: f.email, password: f.password })
    setBusy(false)
    if (signErr) return navigate({ to: '/login' })
    navigate({ to: '/dashboard' })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center"><LanguageMenu /></div>
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('signUp.title')}</h1>
          <p className="mt-1 text-[13.5px] text-token-secondary">{t('signUp.subtitle')}</p>
        </div>

        <form onSubmit={submit} className="space-y-3.5 rounded-[16px] border border-token-subtle bg-token-surface p-6">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-token-secondary">{t('signUp.business')}</label>
            <input value={f.businessName} onChange={set('businessName')} className={inputCls} placeholder={t('signUp.businessPlaceholder')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-token-secondary">{t('signUp.firstName')}</label>
              <input value={f.firstName} onChange={set('firstName')} autoComplete="given-name" className={inputCls} placeholder={t('signUp.firstNamePlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-token-secondary">{t('signUp.lastName')}</label>
              <input value={f.lastName} onChange={set('lastName')} autoComplete="family-name" className={inputCls} placeholder={t('signUp.lastNamePlaceholder')} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-token-secondary">{t('signUp.workEmail')}</label>
            <input type="email" value={f.email} onChange={set('email')} autoComplete="email" className={inputCls} placeholder={t('common.emailPlaceholder')} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-token-secondary">{t('common.password')}</label>
            <input type="password" value={f.password} onChange={set('password')} autoComplete="new-password" className={inputCls} placeholder={t('common.passwordRule')} />
          </div>
          {error && <p className="text-[13px] text-rose-600">{error}</p>}
          <button type="submit" disabled={busy} className={`${btnPrimary} h-10 w-full justify-center`}>{busy ? t('signUp.busy') : t('signUp.button')}</button>
          <p className="pt-1 text-center text-[13px] text-token-secondary">
            {t('signUp.haveAccount')} <Link to="/login" className="font-semibold text-token-primary hover:underline">{t('signUp.signIn')}</Link>
          </p>
          <p className="text-center text-[11.5px] leading-relaxed text-token-secondary">
            {(() => {
              // The sentence order differs between languages, so the links are placed into it.
              const [before, middle, after] = t('signUp.agree', { terms: '\u0000', privacy: '\u0001' }).split(/[\u0000\u0001]/)
              return <>{before}<a href={`${BRAND_URL}/terms`} className="underline">{t('signUp.terms')}</a>{middle}<a href={`${BRAND_URL}/privacy`} className="underline">{t('signUp.privacy')}</a>{after}</>
            })()}
          </p>
        </form>
      </div>
    </div>
  )
}
